import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';

import { db } from '~/config/firebase.config';
import {
    SEASONAL_PREVIEW_KEY,
    SEASONAL_THEME_FLAG,
    SeasonalThemeId,
    isSeasonalClient,
    nextSeasonBoundary,
    seasonAt,
    seasonEnd,
} from '~/config/seasonalTheme.config';

// setTimeout plafonne à ~24,8 jours ; on revérifie au moins toutes les 6 h (comme usePauseActive).
const MAX_WAIT_MS = 6 * 60 * 60 * 1000;

function previewSeason(): SeasonalThemeId | null {
    try {
        return window.localStorage.getItem(SEASONAL_PREVIEW_KEY) === 'halloween' ? 'halloween' : null;
    } catch {
        return null; // stockage bloqué (Safari privé, gotcha #3)
    }
}

/**
 * Thème de saison à appliquer MAINTENANT sur la TV de ce lieu (null = aucun).
 * Se réévalue pile aux bornes de la saison (une TV reste des heures sur le menu) et
 * suit l'interrupteur `features.seasonalTheme` du lieu en direct.
 */
export function useSeasonalTheme(clientID: string | undefined, mode: string | undefined): SeasonalThemeId | null {
    const eligible = isSeasonalClient(clientID, mode);
    const [tick, setTick] = useState(0);
    // 'unknown' tant que la fiche du lieu n'est pas lue : on n'allume pas le thème puis
    // on l'éteint une seconde plus tard (la TV le diffuserait aux téléphones entre-temps).
    // Lu pour CE lieu : un autre lieu (changement d'espace) repart de 'unknown' dès le rendu.
    const [read, setRead] = useState<{ id: string; flag: 'on' | 'off' } | null>(null);
    const flag = read && read.id === clientID ? read.flag : 'unknown';

    useEffect(() => {
        const boundary = nextSeasonBoundary();
        if (!eligible || boundary == null) return undefined;
        const wait = Math.min(Math.max(boundary - Date.now(), 0) + 500, MAX_WAIT_MS);
        const timer = setTimeout(() => setTick((n) => n + 1), wait);
        return () => clearTimeout(timer);
    }, [eligible, tick]);

    useEffect(() => {
        if (!eligible || !clientID) return undefined;
        return onSnapshot(
            doc(db, 'clients', clientID),
            (snap) =>
                setRead({ id: clientID, flag: snap.data()?.features?.[SEASONAL_THEME_FLAG] === false ? 'off' : 'on' }),
            () => {
                // Lecture refusée / hors ligne : on garde la dernière valeur (thème permis si jamais lue).
                setRead((prev) => (prev && prev.id === clientID ? prev : { id: clientID, flag: 'on' }));
            }
        );
    }, [eligible, clientID]);

    if (!eligible || flag !== 'on') return null;
    return seasonAt() ?? (Date.now() < seasonEnd('halloween') ? previewSeason() : null);
}
